import { MouseEvent, ReactElement, useCallback, useEffect, useRef } from 'react';
import { Sidebar } from '@components/common';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { overlayAddFadeAction } from '@components/common/sort';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { useRefetchAllData, UseSyncData } from '@helper/fetchData';
import { dashboardRedirectPaths, meetingPaths, presetRoutePath } from '@components/common/header/index';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useRbdmBudgetAllocated } from '@services/hooks/dashboard/useRbdmBudgetAllocated';
import { useComponentLevelAccessCheck } from '../../../authGuard/hooks/useComponentLevelAccessCheck';

export type IHeaderProps = {
	subRoute?: boolean;
	menuHeading?: string;
};
export const Header = (props: IHeaderProps): ReactElement => {
	const currentBackIndex = useRef<number>(0);
	const currentRouteName = useRef<string>('');
	const { setModal, refetchName } = useSiderToggleMenu();
	const refreshButtonRef = useRef<HTMLButtonElement>(null);
	UseSyncData();
	const { refetchAllData } = useRefetchAllData();
	const { subRoute, menuHeading } = props;
	const navigate = useNavigate();
	const { pathname, state } = useLocation(); // Access the state from useLocation
	const userRole = useUserRoleType();
	const isRBDM = userRole === 'RBDM';

	// Check if we're on any RBDM meeting page
	const isRbdmMeetingPage =
		pathname === '/rbdm/meetings' ||
		pathname === '/rbdm/meetings/todays-meetings' ||
		pathname === '/rbdm/meetings/all-meetings';

	// Always call the hook - React Query will handle caching and only fetch once
	const { currentQuarterData } = useRbdmBudgetAllocated();
	const isLmsRoute = pathname.includes('/lms');
	const hasAccess = useComponentLevelAccessCheck('lms-dashboard');
	const hasCalendarAccess = useComponentLevelAccessCheck('/calendar', true);

	useEffect(() => {
		if (currentRouteName.current === menuHeading) return;

		if (presetRoutePath.find((path) => path.currentPath === pathname)) {
			currentBackIndex.current++;
			currentRouteName.current = menuHeading ?? '';
		}
	}, [menuHeading, pathname]);

	const goBack = (): void => {
		const siteId = state?.siteID; // Access state passed by useLocation
		if (dashboardRedirectPaths.includes(pathname)) {
			navigate('/');
			return;
		}
		const customRedirectPaths: Record<string, { path: string; state?: Record<string, number> }> = {
			'/lms/product-recommendations': { path: '/' },
			'/lms/site-overview': {
				path: '/lms/sites',
				state: siteId ? { siteID: siteId } : undefined,
			},
			'/lms/add-new-site': {
				path: '/lms/sites',
			},
			'/lms/mapview': {
				path: '/lms/sites',
			},
		};

		if (customRedirectPaths[pathname]) {
			const redirectConfig = customRedirectPaths[pathname];
			navigate(redirectConfig.path, { state: redirectConfig.state });
			return;
		}

		const path = presetRoutePath.find((path) => path.currentPath === pathname);
		if (window.history.length == 2 || meetingPaths.includes(pathname)) {
			navigate('/');
			return;
		}
		if (path) {
			navigate(-currentBackIndex.current);
			currentBackIndex.current = 0;
			currentRouteName.current = '';
			return;
		}
		if (window.history.length > 1) {
			navigate(-1);
		}
	};

	const refetchSync = useCallback(
		(e: MouseEvent<HTMLButtonElement>): void => {
			e.preventDefault();
			refetchAllData?.(refetchName);
			if (refreshButtonRef.current) {
				refreshButtonRef.current.classList.add('spin-animation');
				setTimeout(() => {
					if (refreshButtonRef.current) {
						refreshButtonRef.current.classList.remove('spin-animation');
					}
				}, 3000);
			}
		},
		[refetchAllData, refetchName]
	);

	useEffect(() => {
		const intervalId = setInterval(
			() => {
				refetchAllData?.();
			},
			60 * 60 * 1000
		);

		return (): void => clearInterval(intervalId);
		// eslint-disable-next-line
	}, []);

	return (
		<>
			<header className="top-header-wrapper">
				<div className="container">
					<div className="top-header">
						<div className="sidebar-toggle">
							{!subRoute && (
								<button
									type="button"
									aria-label="toggle-button"
									onClick={() => overlayAddFadeAction(() => setModal(true), 'sidebar-overlay')}
									className="navbar-btn"
									value="toggle-button"
								>
									<span className="icon-menu"></span>
								</button>
							)}
							{subRoute && (
								<button
									onClick={goBack}
									type="button"
									id="backButton"
									aria-label="back-button"
									className="navbar-btn hide"
									value="back-button"
								>
									<span className="icon-arrow-left"></span>
								</button>
							)}
						</div>
						<span id="main-header" className={`h3 ${isRBDM && isRbdmMeetingPage ? 'rbdm-header-details' : ''}`}>
							{menuHeading ?? 'MPower'}
						</span>
						<div className="header-calendar-wrapper">
							<button
								type="button"
								id="resync-button"
								ref={refreshButtonRef}
								aria-label="refresh"
								className="refresh-btn"
								onClick={refetchSync}
							>
								<span className="icon-refresh-cw" />
							</button>

							{!subRoute && !isLmsRoute && hasAccess && hasCalendarAccess && !isRBDM && (
								<div className="header-calender">
									<NavLink to="/calendar" aria-label="Go to calender">
										<span className="icon-calendar"></span>
									</NavLink>
								</div>
							)}
						</div>
						{isRBDM && isRbdmMeetingPage && (
							<div className="rbdm-header">
								<div className="rbdm-header-title">
									Budget : <b>{currentQuarterData?.budget_c ?? 0}</b>
								</div>
								<div className="rbdm-header-title">
									Spent : <b>{currentQuarterData?.budget_deduction_c ?? 0}</b>
								</div>
							</div>
						)}
					</div>
				</div>
			</header>
			<Sidebar />
		</>
	);
};
